import React, { useState, useEffect, useRef } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { patientService } from '../../../services/patientService';

const PatientDemographicsForm = ({ 
  formData, 
  onFormChange, 
  errors 
}) => {
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef?.current && !suggestionsRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search patients with debounce
  useEffect(() => {
    if (searchTimeoutRef?.current) {
      clearTimeout(searchTimeoutRef?.current);
    }

    if (patientSearchTerm?.length >= 2) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await patientService?.searchPatients(patientSearchTerm);
          setPatientSuggestions(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error searching patients:', error);
          setPatientSuggestions([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    } else {
      setPatientSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef?.current) {
        clearTimeout(searchTimeoutRef?.current);
      }
    };
  }, [patientSearchTerm]);

  // Handle patient selection from dropdown
  const handlePatientSelect = (patient) => {
    setPatientSearchTerm(patient?.patientName);
    onFormChange('patientName', patient?.patientName);
    onFormChange('contactNumber', patient?.contactNumber);
    onFormChange('patientEmail', patient?.email || '');
    
    // Auto-populate date of birth and gender if available
    if (patient?.dateOfBirth) {
      const birthDate = new Date(patient.dateOfBirth);
      const today = new Date();
      let age = today?.getFullYear() - birthDate?.getFullYear();
      const monthDiff = today?.getMonth() - birthDate?.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
        age--;
      }
      onFormChange('patientAge', age?.toString());
    }
    
    if (patient?.gender) {
      onFormChange('gender', patient?.gender);
    }

    setShowSuggestions(false);
    setPatientSuggestions([]);
  };

  // Handle patient name input change
  const handlePatientNameChange = (e) => {
    const value = e?.target?.value || '';
    setPatientSearchTerm(value);
    onFormChange('patientName', value);
  };

  const ageGroupOptions = [
    { value: 'neonate', label: 'Neonate (0-28 days)' },
    { value: 'infant', label: 'Infant (1-12 months)' },
    { value: 'child', label: 'Child (1-12 years)' },
    { value: 'adolescent', label: 'Adolescent (13-17 years)' },
    { value: 'adult', label: 'Adult (18-64 years)' },
    { value: 'elderly', label: 'Elderly (65+ years)' }
  ];

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
  ];

  const insuranceProviders = [
    { value: '', label: 'Select Insurance Provider' },
    { value: 'sagicor', label: 'Sagicor' },
    { value: 'guardian-group', label: 'Guardian Group' },
    { value: 'nagico', label: 'Nagico' },
    { value: 'cg-united', label: 'CG United' },
    { value: 'uninsured', label: 'Uninsured / Self-Pay' },
    { value: 'other', label: 'Other Insurance' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="User" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Patient Demographics</h2>
          <p className="text-sm text-muted-foreground">Basic patient information for clinical assessment</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Patient Name with Autocomplete */}
        <div className="relative" ref={suggestionsRef}>
          <Input
            label="Patient Name"
            type="text"
            placeholder="Start typing patient name..."
            value={patientSearchTerm || formData?.patientName || ''}
            onChange={handlePatientNameChange}
            error={errors?.patientName}
            required
            description="Type to search existing patients or enter new patient name"
          />
          {isSearching && (
            <div className="absolute right-3 top-9 text-muted-foreground">
              <Icon name="Loader" size={16} className="animate-spin" />
            </div>
          )}
          {showSuggestions && patientSuggestions?.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {patientSuggestions?.map((patient) => (
                <button
                  key={patient?.id}
                  type="button"
                  onClick={() => handlePatientSelect(patient)}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 focus:outline-none focus:bg-muted/70"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">{patient?.patientName}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {patient?.contactNumber}
                        {patient?.email && ` • ${patient?.email}`}
                      </p>
                      {patient?.dateOfBirth && (
                        <p className="text-xs text-muted-foreground">
                          DOB: {new Date(patient.dateOfBirth)?.toLocaleDateString()}
                          {patient?.gender && ` • ${patient?.gender?.charAt(0)?.toUpperCase() + patient?.gender?.slice(1)}`}
                        </p>
                      )}
                    </div>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground mt-1" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Contact Number */}
        <Input
          label="Contact Number"
          type="tel"
          placeholder="Enter contact number"
          value={formData?.contactNumber}
          onChange={(e) => onFormChange('contactNumber', e?.target?.value)}
          error={errors?.contactNumber}
          required
          description="Patient's primary contact number"
        />

        {/* Patient Email (Optional) */}
        <Input
          label="Patient Email (Optional)"
          type="email"
          placeholder="Enter email address"
          value={formData?.patientEmail}
          onChange={(e) => onFormChange('patientEmail', e?.target?.value)}
          error={errors?.patientEmail}
          description="Optional email for patient communication"
        />

        <Input
          label="Patient ID"
          type="text"
          placeholder="Enter patient identifier"
          value={formData?.patientId}
          onChange={(e) => onFormChange('patientId', e?.target?.value)}
          error={errors?.patientId}
          required
          description="Hospital or clinic patient identifier"
        />

        <Input
          label="Patient Age (years)"
          type="number"
          placeholder="Enter patient age"
          value={formData?.patientAge}
          onChange={(e) => onFormChange('patientAge', e?.target?.value)}
          error={errors?.patientAge}
          required
          min="0"
          max="120"
          description="Patient's age in years for clinical assessment"
        />

        <Select
          label="Gender"
          options={genderOptions}
          value={formData?.gender}
          onChange={(value) => onFormChange('gender', value)}
          error={errors?.gender}
          required
          placeholder="Select gender"
        />

        <Select
          label="Insurance Provider"
          options={insuranceProviders}
          value={formData?.insuranceProvider}
          onChange={(value) => onFormChange('insuranceProvider', value)}
          error={errors?.insuranceProvider}
          required
          placeholder="Select insurance provider"
          description="Select patient's insurance coverage"
        />

        <Input
          label="Weight (kg)"
          type="number"
          placeholder="Enter weight"
          value={formData?.weight}
          onChange={(e) => onFormChange('weight', e?.target?.value)}
          error={errors?.weight}
          min="0"
          max="500"
          description="Patient weight for dosing considerations"
        />
      </div>
      {formData?.insuranceProvider === 'uninsured' && (
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="#3b82f6" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                Medical Tourism Options Available
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                For uninsured patients, consider exploring regional medical tourism facilities with competitive pricing.
                <a href="/regional-network-directory" className="ml-1 underline font-medium">
                  View Regional Directory
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 space-y-4">
        <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border">
          <Checkbox
            id="isEmergency"
            checked={formData?.isEmergency}
            onCheckedChange={(checked) => onFormChange('isEmergency', checked)}
          />
          <div className="flex-1">
            <label htmlFor="isEmergency" className="text-sm font-medium text-foreground cursor-pointer">
              Emergency Case
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Mark if this is an emergency requiring immediate imaging
            </p>
          </div>
        </div>

        {formData?.gender === 'female' && (
          <div className="flex items-start gap-3 p-4 bg-muted/30 rounded-lg border border-border">
            <Checkbox
              id="isPregnant"
              checked={formData?.isPregnant}
              onCheckedChange={(checked) => onFormChange('isPregnant', checked)}
            />
            <div className="flex-1">
              <label htmlFor="isPregnant" className="text-sm font-medium text-foreground cursor-pointer">
                Pregnant or Possibly Pregnant
              </label>
              <p className="text-xs text-muted-foreground mt-1">
                Critical for radiation safety - only radiation-free imaging will be recommended
              </p>
            </div>
          </div>
        )}

        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <Checkbox
            id="hasPacemaker"
            checked={formData?.hasPacemaker}
            onCheckedChange={(checked) => onFormChange('hasPacemaker', checked)}
          />
          <div className="flex-1">
            <label htmlFor="hasPacemaker" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
              <Icon name="Heart" size={16} className="text-amber-600 dark:text-amber-400" />
              Pacemaker or Implanted Device
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Patient has pacemaker, ICD, cochlear implant, or other MRI-incompatible device (MRI will be contraindicated unless device is MRI-conditional)
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <Checkbox
            id="hasMetalImplants"
            checked={formData?.hasMetalImplants}
            onCheckedChange={(checked) => {
              onFormChange('hasMetalImplants', checked);
              if (!checked) {
                onFormChange('metalImplantTypes', []);
              }
            }}
          />
          <div className="flex-1">
            <label htmlFor="hasMetalImplants" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
              <Icon name="AlertTriangle" size={16} className="text-purple-600 dark:text-purple-400" />
              Metal Implants Present
            </label>
            <p className="text-xs text-muted-foreground mt-1">
              Patient has metal implants that may affect MRI safety (ferromagnetic materials are contraindicated)
            </p>
            
            {formData?.hasMetalImplants && (
              <div className="mt-3">
                <label className="text-xs font-medium text-foreground mb-2 block">
                  Select Metal Implant Type(s):
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { value: 'hip-prosthesis', label: 'Hip Prosthesis (Titanium/Cobalt-Chrome)' },
                    { value: 'knee-prosthesis', label: 'Knee Prosthesis (Titanium/Cobalt-Chrome)' },
                    { value: 'spinal-rods', label: 'Spinal Rods/Screws (Titanium/Stainless Steel)' },
                    { value: 'dental-implants', label: 'Dental Implants (Titanium)' },
                    { value: 'surgical-clips', label: 'Surgical Clips/Staples (Titanium/Stainless Steel)' },
                    { value: 'joint-pins', label: 'Joint Pins/Plates (Titanium/Stainless Steel)' },
                    { value: 'stents-cardiac', label: 'Cardiac Stents (Stainless Steel/Nitinol)' },
                    { value: 'ivc-filter', label: 'IVC Filter (Stainless Steel/Nitinol)' },
                    { value: 'aneurysm-clips', label: 'Aneurysm Clips (Titanium/Cobalt Alloy)' },
                    { value: 'cochlear-implant', label: 'Cochlear Implant (Titanium/Platinum)' },
                    { value: 'bone-screws', label: 'Bone Screws/Nails (Titanium/Stainless Steel)' },
                    { value: 'shrapnel', label: 'Shrapnel/Bullet Fragments (Various Metals)' },
                    { value: 'other', label: 'Other Metal Implant' }
                  ]?.map((implant) => (
                    <div key={implant?.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`implant-${implant?.value}`}
                        checked={formData?.metalImplantTypes?.includes(implant?.value)}
                        onCheckedChange={(checked) => {
                          const currentTypes = formData?.metalImplantTypes || [];
                          const newTypes = checked
                            ? [...currentTypes, implant?.value]
                            : currentTypes?.filter(t => t !== implant?.value);
                          onFormChange('metalImplantTypes', newTypes);
                        }}
                      />
                      <label
                        htmlFor={`implant-${implant?.value}`}
                        className="text-xs text-foreground cursor-pointer"
                      >
                        {implant?.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDemographicsForm;